/**
 * WordPress dependencies
 */
import {useState, useEffect} from '@wordpress/element';
import {__} from '@wordpress/i18n';
import {
    Card,
    CardHeader,
    CardBody,
    TextControl,
    SelectControl,
    TextareaControl,
    Button,
    Notice,
    Spinner
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

/**
 * Slot-based event types that use availability schedules and time slots
 */
const SLOT_BASED_TYPES = ['one-on-one', 'group'];

/**
 * Create Booking Form component
 *
 * @param {Object}   props
 * @param {Function} props.onSave   Callback when booking is created.
 * @param {Function} props.onCancel Callback when form is cancelled.
 * @return {JSX.Element} The CreateBookingForm component.
 */
const CreateBookingForm = ( {onSave, onCancel} ) => {
    const [formData, setFormData] = useState( {
        event_id: '',
        attendee_name: '',
        attendee_email: '',
        attendee_phone: '',
        booking_date: '',
        booking_start: '',
        booking_end: '',
        booking_status: 'pending',
        admin_payment_action: '',
        booking_meta: {}
    } );
    const [events, setEvents] = useState( [] );
    const [selectedEvent, setSelectedEvent] = useState( null );
    const [isLoading, setIsLoading] = useState( false );
    const [isSaving, setIsSaving] = useState( false );
    const [notices, setNotices] = useState( [] );
    const [fieldErrors, setFieldErrors] = useState( {} );

    // Slot-based booking state
    const [isSlotBased, setIsSlotBased] = useState( false );
    const [bookableDates, setBookableDates] = useState( [] );
    const [availableSlots, setAvailableSlots] = useState( [] );
    const [loadingDates, setLoadingDates] = useState( false );
    const [loadingSlots, setLoadingSlots] = useState( false );

    // Check if the selected event is a paid event
    const isPaidEvent = selectedEvent && parseFloat( selectedEvent.price ) > 0;

    // Payment handling options for admin-created bookings on paid events
    const paymentActionOptions = [
        { label: __( 'Select payment action...', 'nemtly-booking' ), value: '' },
        { label: __( 'Manual / Offline (payment already collected)', 'nemtly-booking' ), value: 'manual_offline' },
        { label: __( 'Waive Payment (no payment required)', 'nemtly-booking' ), value: 'waive' },
    ];

    // Booking status options
    const statusOptions = [
        {label: __( 'Pending', 'nemtly-booking' ), value: 'pending'},
        {label: __( 'Confirmed', 'nemtly-booking' ), value: 'confirmed'},
        {label: __( 'Cancelled', 'nemtly-booking' ), value: 'cancelled'}
    ];

    // Load available events
    useEffect( () => {
        loadEvents();
    }, [] );

    /**
     * Load available events from API
     */
    const loadEvents = async () => {
        try {
            setIsLoading( true );
            const response = await apiFetch( {
                path: '/nemtly-booking/v1/events?per_page=100&status=publish'
            } );

            if (response.success && response.result) {
                setEvents( Array.isArray( response.result ) ? response.result : [] );
            } else {
                throw new Error( response.message || __( 'Failed to load events.', 'nemtly-booking' ) );
            }
        } catch (error) {
            setNotices( [{
                type: 'error',
                message: error.message || __( 'Failed to load events.', 'nemtly-booking' )
            }] );
        } finally {
            setIsLoading( false );
        }
    };

    /**
     * Fetch bookable dates for a slot-based event
     *
     * @param {number} eventId The event ID.
     */
    const fetchBookableDates = async ( eventId ) => {
        setLoadingDates( true );
        setBookableDates( [] );
        setAvailableSlots( [] );
        try {
            const response = await apiFetch( {
                path: `/nemtly-booking/v1/events/${eventId}/dates?limit=30`
            } );
            if (response.success && response.result) {
                setBookableDates( response.result.dates || [] );
            }
        } catch (error) {
            setNotices( [{
                type: 'error',
                message: error.message || __( 'Failed to load bookable dates.', 'nemtly-booking' )
            }] );
        } finally {
            setLoadingDates( false );
        }
    };

    /**
     * Fetch available time slots for a specific date
     *
     * @param {number} eventId The event ID.
     * @param {string} date    The date (Y-m-d).
     */
    const fetchAvailableSlots = async ( eventId, date ) => {
        setLoadingSlots( true );
        setAvailableSlots( [] );
        try {
            const response = await apiFetch( {
                path: `/nemtly-booking/v1/events/${eventId}/slots?date=${date}`
            } );
            if (response.success && response.result) {
                setAvailableSlots( response.result.slots || [] );
            }
        } catch (error) {
            setNotices( [{
                type: 'error',
                message: error.message || __( 'Failed to load time slots.', 'nemtly-booking' )
            }] );
        } finally {
            setLoadingSlots( false );
        }
    };

    /**
     * Handle form input changes
     *
     * @param {string} field The field name.
     * @param {*}      value The new value.
     */
    const handleInputChange = ( field, value ) => {
        // Clear field error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors( prev => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            } );
        }

        if (field === 'event_id') {
            const event = events.find( e => e.id.toString() === value );
            setSelectedEvent( event );

            const eventIsSlotBased = event && SLOT_BASED_TYPES.includes( event.event_type );
            setIsSlotBased( eventIsSlotBased );

            // Reset slot state
            setAvailableSlots( [] );
            setBookableDates( [] );

            if (event && eventIsSlotBased) {
                // Slot-based: fetch bookable dates, don't pre-fill times
                fetchBookableDates( event.id );
                setFormData( prev => ( {
                    ...prev,
                    [field]: value,
                    booking_date: '',
                    booking_start: '',
                    booking_end: '',
                    admin_payment_action: ''
                } ) );
            } else if (event) {
                // Date-based: pre-fill from event data
                const eventDate = event.event_date ? event.event_date.split( ' ' )[0] : '';
                const startTime = event.event_time ? event.event_time.replace( /:00$/, '' ) : '';
                const endTime = event.end_time ? event.end_time.replace( /:00$/, '' ) : '';

                setFormData( prev => ( {
                    ...prev,
                    [field]: value,
                    booking_date: eventDate,
                    booking_start: startTime,
                    booking_end: endTime,
                    admin_payment_action: ''
                } ) );
            } else {
                setFormData( prev => ( {
                    ...prev,
                    [field]: value,
                    booking_date: '',
                    booking_start: '',
                    booking_end: '',
                    admin_payment_action: ''
                } ) );
            }
        } else if (field === 'booking_date' && isSlotBased) {
            // When date changes for slot-based event, validate and fetch slots
            setFormData( prev => ( {
                ...prev,
                booking_date: value,
                booking_start: '',
                booking_end: ''
            } ) );
            setAvailableSlots( [] );

            if (value && formData.event_id) {
                if (bookableDates.length > 0 && !bookableDates.includes( value )) {
                    // Selected date doesn't match the event's availability schedule
                    const dayName = new Date( value + 'T00:00:00' ).toLocaleDateString( 'en-US', { weekday: 'long' } );
                    setFieldErrors( prev => ( {
                        ...prev,
                        booking_date: `${dayName}s ${__( 'are not available for this event.', 'nemtly-booking' )}`
                    } ) );
                } else {
                    fetchAvailableSlots( formData.event_id, value );
                }
            }
        } else {
            setFormData( prev => ( {
                ...prev,
                [field]: value
            } ) );
        }
    };

    /**
     * Handle slot selection
     *
     * @param {Object} slot The selected slot object.
     */
    const handleSlotSelect = ( slot ) => {
        if (!slot.available) return;

        // Clear slot field error
        if (fieldErrors.booking_start) {
            setFieldErrors( prev => {
                const updated = { ...prev };
                delete updated.booking_start;
                return updated;
            } );
        }

        setFormData( prev => ( {
            ...prev,
            booking_start: slot.start,
            booking_end: slot.end
        } ) );
    };

    /**
     * Validate form data
     *
     * @return {Object} Object mapping field names to error messages. Empty if valid.
     */
    const validateForm = () => {
        const errors = {};

        if (!formData.event_id) {
            errors.event_id = __( 'Please select an event.', 'nemtly-booking' );
        }

        if (!formData.attendee_name.trim()) {
            errors.attendee_name = __( 'Attendee name is required.', 'nemtly-booking' );
        }

        if (!formData.attendee_email.trim()) {
            errors.attendee_email = __( 'Attendee email is required.', 'nemtly-booking' );
        } else if (!isValidEmail( formData.attendee_email )) {
            errors.attendee_email = __( 'Please enter a valid email address.', 'nemtly-booking' );
        }

        if (!formData.booking_date) {
            errors.booking_date = __( 'Booking date is required.', 'nemtly-booking' );
        }

        // Validate slot selection for slot-based events
        if (isSlotBased && !formData.booking_start) {
            errors.booking_start = __( 'Please select a time slot.', 'nemtly-booking' );
        }

        // Validate payment action for paid events
        if (isPaidEvent && !formData.admin_payment_action) {
            errors.admin_payment_action = __( 'Please select a payment action for this paid event.', 'nemtly-booking' );
        }

        return errors;
    };

    /**
     * Validate email format
     *
     * @param {string} email Email to validate.
     * @return {boolean} True if email is valid.
     */
    const isValidEmail = ( email ) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test( email );
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async () => {
        const errors = validateForm();
        if (Object.keys( errors ).length > 0) {
            setFieldErrors( errors );
            return;
        }

        setFieldErrors( {} );

        try {
            setIsSaving( true );
            setNotices( [] );

            const response = await apiFetch( {
                path: '/nemtly-booking/v1/bookings',
                method: 'POST',
                data: formData
            } );

            if (response.success) {
                setNotices( [{
                    type: 'success',
                    message: __( 'Booking created successfully.', 'nemtly-booking' )
                }] );

                if (onSave) {
                    onSave( response.result );
                }
            } else {
                throw new Error( response.message || __( 'Failed to create booking.', 'nemtly-booking' ) );
            }
        } catch (error) {
            setNotices( [{
                type: 'error',
                message: error.message || __( 'Failed to create booking.', 'nemtly-booking' )
            }] );
        } finally {
            setIsSaving( false );
        }
    };

    /**
     * Format date for display
     *
     * @param {string} dateString The date string to format.
     * @return {string} Formatted date string.
     */
    const formatDisplayDate = ( dateString ) => {
        if (!dateString) return '';
        const date = new Date( dateString );
        return date.toLocaleDateString( 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        } );
    };

    /**
     * Format time for display
     *
     * @param {string} timeString The time string to format (HH:mm).
     * @return {string} Formatted time string (e.g., 10:00 AM).
     */
    const formatDisplayTime = ( timeString ) => {
        if (!timeString) return '';
        const [hours, minutes] = timeString.split( ':' );
        const date = new Date();
        date.setHours( hours );
        date.setMinutes( minutes );
        return date.toLocaleTimeString( 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        } );
    };

    /**
     * Format date for dropdown label (e.g., "Mon, Mar 20, 2026")
     *
     * @param {string} dateString The date string (Y-m-d).
     * @return {string} Formatted label.
     */
    const formatDateLabel = ( dateString ) => {
        if (!dateString) return '';
        const date = new Date( dateString + 'T00:00:00' );
        return date.toLocaleDateString( 'en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        } );
    };

    if (isLoading) {
        return (
            <div className="nb-text-center nb-p-8">
                <Spinner />
                <p>{__( 'Loading events...', 'nemtly-booking' )}</p>
            </div>
        );
    }

    // Prepare event options for select
    const eventOptions = [
        {label: __( 'Select an event...', 'nemtly-booking' ), value: ''},
        ...events.map( event => ( {
            label: event.title?.rendered || event.title || `Event #${event.id}`,
            value: event.id.toString()
        } ) )
    ];

    return (
        <div className="nb-create-booking-form nb-max-w-4xl nb-mx-auto nb-p-6">
            {notices.map( ( notice, index ) => (
                <Notice
                    key={index}
                    status={notice.type}
                    onRemove={() => setNotices( prev => prev.filter( ( _, i ) => i !== index ) )}
                >
                    {notice.message}
                </Notice>
            ) )}

            <Card>
                <CardHeader>
                    <h2>{__( 'Booking Details', 'nemtly-booking' )}</h2>
                </CardHeader>
                <CardBody>
                    <div className={`nb-form-row ${fieldErrors.event_id ? 'nb-field-error' : ''}`}>
                        <SelectControl
                            label={__( 'Event', 'nemtly-booking' )}
                            value={formData.event_id}
                            options={eventOptions}
                            onChange={( value ) => handleInputChange( 'event_id', value )}
                            help={fieldErrors.event_id || ''}
                            className={fieldErrors.event_id ? 'nb-has-error' : ''}
                            required
                        />
                    </div>

                    {isPaidEvent && (
                        <div className="nb-form-row nb-bg-gray-50 nb-p-4 nb-rounded-lg nb-border nb-border-gray-200">
                            <div className="nb-mb-3 nb-text-sm nb-text-gray-700">
                                <strong>{__( 'This is a paid event', 'nemtly-booking' )}</strong>
                                {' — '}
                                {selectedEvent.currency || 'USD'} {parseFloat( selectedEvent.price ).toFixed( 2 )}
                            </div>
                            <div className={fieldErrors.admin_payment_action ? 'nb-field-error' : ''}>
                                <SelectControl
                                    label={__( 'Payment Handling', 'nemtly-booking' )}
                                    value={formData.admin_payment_action}
                                    options={paymentActionOptions}
                                    onChange={( value ) => handleInputChange( 'admin_payment_action', value )}
                                    help={fieldErrors.admin_payment_action || __( 'Choose how to handle payment for this booking.', 'nemtly-booking' )}
                                    className={fieldErrors.admin_payment_action ? 'nb-has-error' : ''}
                                    required
                                />
                            </div>
                        </div>
                    )}

                    <div className={`nb-form-row ${fieldErrors.attendee_name ? 'nb-field-error' : ''}`}>
                        <TextControl
                            label={__( 'Attendee Name', 'nemtly-booking' )}
                            value={formData.attendee_name}
                            onChange={( value ) => handleInputChange( 'attendee_name', value )}
                            help={fieldErrors.attendee_name || ''}
                            className={fieldErrors.attendee_name ? 'nb-has-error' : ''}
                            required
                        />
                    </div>

                    <div className={`nb-form-row ${fieldErrors.attendee_email ? 'nb-field-error' : ''}`}>
                        <TextControl
                            label={__( 'Attendee Email', 'nemtly-booking' )}
                            type="email"
                            value={formData.attendee_email}
                            onChange={( value ) => handleInputChange( 'attendee_email', value )}
                            help={fieldErrors.attendee_email || ''}
                            className={fieldErrors.attendee_email ? 'nb-has-error' : ''}
                            required
                        />
                    </div>

                    <div className="nb-form-row">
                        <TextControl
                            label={__( 'Attendee Phone', 'nemtly-booking' )}
                            value={formData.attendee_phone}
                            onChange={( value ) => handleInputChange( 'attendee_phone', value )}
                        />
                    </div>

                    {/* Date selection — slot-based uses datepicker with available days hint, date-based uses plain calendar input */}
                    {isSlotBased ? (
                        <div className={`nb-form-row ${fieldErrors.booking_date ? 'nb-field-error' : ''}`}>
                            {loadingDates ? (
                                <div className="nb-flex nb-items-center nb-gap-2 nb-py-2">
                                    <Spinner />
                                    <span className="nb-text-sm nb-text-gray-500">{__( 'Loading available dates...', 'nemtly-booking' )}</span>
                                </div>
                            ) : bookableDates.length > 0 ? (
                                <>
                                    <TextControl
                                        label={__( 'Booking Date', 'nemtly-booking' )}
                                        type="date"
                                        value={formData.booking_date}
                                        onChange={( value ) => handleInputChange( 'booking_date', value )}
                                        help={fieldErrors.booking_date || ''}
                                        className={fieldErrors.booking_date ? 'nb-has-error' : ''}
                                        required
                                    />
                                    <div className="nb-mt-1 nb-text-xs nb-text-gray-500">
                                        {__( 'Available dates:', 'nemtly-booking' )}{' '}
                                        {bookableDates.slice( 0, 5 ).map( ( d ) => formatDateLabel( d ) ).join( ', ' )}
                                        {bookableDates.length > 5 && ` ${__( 'and', 'nemtly-booking' )} ${bookableDates.length - 5} ${__( 'more', 'nemtly-booking' )}`}
                                    </div>
                                </>
                            ) : selectedEvent ? (
                                <p className="nb-text-sm nb-text-gray-500">
                                    {__( 'No available dates found for this event.', 'nemtly-booking' )}
                                </p>
                            ) : null}
                        </div>
                    ) : (
                        <div className={`nb-form-row ${fieldErrors.booking_date ? 'nb-field-error' : ''}`}>
                            <div className="nb-flex nb-items-center nb-gap-4">
                                <TextControl
                                    label={__( 'Booking Date', 'nemtly-booking' )}
                                    type="date"
                                    value={formData.booking_date}
                                    onChange={( value ) => handleInputChange( 'booking_date', value )}
                                    help={fieldErrors.booking_date || ''}
                                    className={fieldErrors.booking_date ? 'nb-has-error' : ''}
                                    required
                                />
                                {selectedEvent && (
                                    <div className="nb-text-sm">
                                        <p className="nb-font-medium nb-text-gray-700 nb-m-0">{__( 'Event Date', 'nemtly-booking' )}</p>
                                        <p className="nb-text-orange-600 nb-m-0">
                                            {formatDisplayDate( selectedEvent.event_date )} {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.event_date ? `${__( 'to', 'nemtly-booking' )} ${formatDisplayDate( selectedEvent.end_date )}` : ''}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Slot picker for slot-based events */}
                    {isSlotBased && formData.booking_date && (
                        <div className={`nb-form-row ${fieldErrors.booking_start ? 'nb-field-error' : ''}`}>
                            <label className="nb-block nb-text-sm nb-font-medium nb-text-gray-700 nb-mb-2">
                                {__( 'Time Slot', 'nemtly-booking' )}
                            </label>
                            {loadingSlots ? (
                                <div className="nb-flex nb-items-center nb-gap-2 nb-py-2">
                                    <Spinner />
                                    <span className="nb-text-sm nb-text-gray-500">{__( 'Loading time slots...', 'nemtly-booking' )}</span>
                                </div>
                            ) : availableSlots.length > 0 ? (
                                <>
                                    <div className="nb-slot-picker-grid">
                                        {availableSlots.map( ( slot ) => {
                                            const isSelected = formData.booking_start === slot.start;
                                            const isFull = !slot.available;

                                            let slotClass = 'nb-slot-btn';
                                            if (isFull) {
                                                slotClass += ' nb-slot-btn--full';
                                            } else if (isSelected) {
                                                slotClass += ' nb-slot-btn--selected';
                                            }

                                            return (
                                                <button
                                                    key={slot.start}
                                                    type="button"
                                                    className={slotClass}
                                                    onClick={() => handleSlotSelect( slot )}
                                                    disabled={isFull}
                                                >
                                                    <div className="nb-slot-btn__time">
                                                        {formatDisplayTime( slot.start )} – {formatDisplayTime( slot.end )}
                                                    </div>
                                                    <div className="nb-slot-btn__info">
                                                        {isFull
                                                            ? __( 'Full', 'nemtly-booking' )
                                                            : slot.total_capacity === 1
                                                                ? __( 'Available', 'nemtly-booking' )
                                                                : slot.spots_remaining >= 999
                                                                    ? __( 'Available', 'nemtly-booking' )
                                                                    : slot.spots_remaining === 1
                                                                        ? __( '1 spot left', 'nemtly-booking' )
                                                                        : `${slot.spots_remaining} ${__( 'spots', 'nemtly-booking' )}`
                                                        }
                                                    </div>
                                                </button>
                                            );
                                        } )}
                                    </div>
                                    {fieldErrors.booking_start && (
                                        <p className="nb-text-sm nb-text-red-600 nb-mt-1">{fieldErrors.booking_start}</p>
                                    )}
                                </>
                            ) : (
                                <p className="nb-text-sm nb-text-gray-500">
                                    {__( 'No time slots available for this date.', 'nemtly-booking' )}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Manual time inputs — only for non-slot-based events */}
                    {!isSlotBased && (
                        <>
                            <div className="nb-form-row">
                                <div className="nb-flex nb-items-center nb-gap-4">
                                    <div>
                                        <TextControl
                                            label={__( 'Start Time', 'nemtly-booking' )}
                                            type="time"
                                            value={formData.booking_start}
                                            onChange={( value ) => handleInputChange( 'booking_start', value )}
                                        />
                                    </div>
                                    {selectedEvent && selectedEvent.event_time && (
                                        <div className="nb-mt-6 nb-text-sm">
                                            <div className="nb-font-medium nb-text-gray-700">{__( 'Event Time', 'nemtly-booking' )}</div>
                                            <div className="nb-text-orange-600">
                                                {__( 'From', 'nemtly-booking' )} {formatDisplayTime( selectedEvent.event_time )} {selectedEvent.end_time && selectedEvent.end_time !== selectedEvent.event_time ? `${__( 'to', 'nemtly-booking' )} ${formatDisplayTime( selectedEvent.end_time )}` : ''}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="nb-form-row">
                                <TextControl
                                    label={__( 'End Time', 'nemtly-booking' )}
                                    type="time"
                                    value={formData.booking_end}
                                    onChange={( value ) => handleInputChange( 'booking_end', value )}
                                />
                            </div>
                        </>
                    )}

                    {!isPaidEvent && (
                        <div className="nb-form-row">
                            <SelectControl
                                label={__( 'Status', 'nemtly-booking' )}
                                value={formData.booking_status}
                                options={statusOptions}
                                onChange={( value ) => handleInputChange( 'booking_status', value )}
                            />
                        </div>
                    )}

                    <div className="nb-form-row">
                        <TextareaControl
                            label={__( 'Additional Notes', 'nemtly-booking' )}
                            value={formData.booking_meta?.notes || ''}
                            onChange={( value ) => handleInputChange( 'booking_meta', {
                                ...formData.booking_meta,
                                notes: value
                            } )}
                            rows={4}
                        />
                    </div>

                    <div className="nb-form-actions">
                        <Button
                            isPrimary
                            onClick={handleSubmit}
                            isBusy={isSaving}
                            disabled={isSaving}
                        >
                            {isSaving ? __( 'Creating...', 'nemtly-booking' ) : __( 'Create Booking', 'nemtly-booking' )}
                        </Button>
                        <Button
                            isSecondary
                            onClick={onCancel}
                            disabled={isSaving}
                        >
                            {__( 'Cancel', 'nemtly-booking' )}
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default CreateBookingForm;
